import React from "react";
import { Calendar, Clock } from "lucide-react";
import {
    SCHEDULE_TYPES,
    WEEKDAY_OPTIONS,
} from "@/hooks/useDutyCheckSchedule";

export default function DutyCheckSchedulePicker({ schedule, patchSchedule, summary }) {
    const isScheduled = schedule.deliveryMode === "scheduled";

    return (
        <div className="space-y-3">
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600" />
                Sending Schedule
            </label>

            <div className="flex gap-2 p-1 bg-gray-50 rounded-2xl border border-gray-100">
                <button
                    type="button"
                    onClick={() => patchSchedule({ deliveryMode: "now" })}
                    className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-all ${schedule.deliveryMode === "now" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
                >
                    Send Now
                </button>
                <button
                    type="button"
                    onClick={() => patchSchedule({ deliveryMode: "scheduled" })}
                    className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-all ${schedule.deliveryMode === "scheduled" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
                >
                    Schedule
                </button>
            </div>

            {isScheduled && (
                <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                    <div className="grid grid-cols-2 gap-2">
                        {SCHEDULE_TYPES.map((type) => (
                            <button
                                key={type.id}
                                type="button"
                                onClick={() => patchSchedule({ scheduleType: type.id })}
                                className={`py-2 px-3 rounded-xl border text-xs font-semibold transition-all ${schedule.scheduleType === type.id
                                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                                    : "border-gray-100 bg-white text-gray-500 hover:border-gray-200"
                                    }`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>

                    <div className="relative">
                        <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                            type="time"
                            value={schedule.time}
                            onChange={(e) => patchSchedule({ time: e.target.value })}
                            className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all"
                        />
                    </div>

                    {schedule.scheduleType === "custom" && (
                        <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 space-y-3">
                            <div className="flex gap-2 p-1 bg-white rounded-xl border border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => patchSchedule({ customRecurring: true })}
                                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${schedule.customRecurring ? "bg-indigo-50 text-indigo-700" : "text-gray-400"}`}
                                >
                                    Recurring
                                </button>
                                <button
                                    type="button"
                                    onClick={() => patchSchedule({ customRecurring: false })}
                                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${!schedule.customRecurring ? "bg-indigo-50 text-indigo-700" : "text-gray-400"}`}
                                >
                                    One-time
                                </button>
                            </div>

                            {schedule.customRecurring ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {WEEKDAY_OPTIONS.map((day) => {
                                        const selected = schedule.customWeekdays?.includes(day.id);
                                        return (
                                            <button
                                                key={day.id}
                                                type="button"
                                                onClick={() => {
                                                    const current = schedule.customWeekdays || [];
                                                    const next = selected
                                                        ? current.filter((d) => d !== day.id)
                                                        : [...current, day.id];
                                                    patchSchedule({ customWeekdays: next });
                                                }}
                                                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${selected
                                                    ? "bg-indigo-600 border-indigo-600 text-white"
                                                    : "bg-white border-gray-200 text-gray-500 hover:border-indigo-200"
                                                    }`}
                                            >
                                                {day.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="relative">
                                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <input
                                        type="date"
                                        value={schedule.customDate}
                                        onChange={(e) => patchSchedule({ customDate: e.target.value })}
                                        min={new Date().toISOString().slice(0, 10)}
                                        className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="px-3 py-2 rounded-xl bg-indigo-50/80 border border-indigo-100 text-xs text-indigo-800">
                        <span className="font-semibold">Summary: </span>
                        {summary}
                    </div>
                </div>
            )}
        </div>
    );
}
